import { useState } from 'react';
import s from './Settings.module.css';
import { useAuth } from 'contexts/AuthContext';
import { useCredits } from 'contexts/CreditsContext';
import { tryFetchWithAuth } from 'utils/fetchWithAuth';
import { useLogout } from 'hooks/handleLogout';
import { Link } from 'react-router-dom';

const formatNum = (n) => (n ?? 0).toLocaleString();

const EyeIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512">
        <path d="M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z" />
    </svg>
);

const EyeOffIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 640 512">
        <path d="M38.8 5.1C28.4-3.1 13.3-1.2 5.1 9.2S-1.2 34.7 9.2 42.9l592 464c10.4 8.2 25.5 6.3 33.7-4.1s6.3-25.5-4.1-33.7L525.6 386.7c39.6-40.6 66.4-86.1 79.9-118.4c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C465.5 68.8 400.8 32 320 32c-68.2 0-125 26.3-169.3 60.8L38.8 5.1zM223.1 149.5C248.6 126.2 282.7 112 320 112c79.5 0 144 64.5 144 144c0 24.9-6.3 48.3-17.4 68.7L408 294.5c8.4-19.3 10.6-41.4 4.8-63.3c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3c0 10.2-2.4 19.8-6.6 28.3l-90.3-70.8zM373 389.9c-16.4 6.5-34.3 10.1-53 10.1c-79.5 0-144-64.5-144-144c0-6.9 .5-13.6 1.4-20.2L83.1 161.5C60.3 191.2 44 220.8 34.5 243.7c-3.3 7.9-3.3 16.7 0 24.6c14.9 35.7 46.2 87.7 93 131.1C174.5 443.2 239.2 480 320 480c47.8 0 89.9-12.9 126.2-32.5L373 389.9z" />
    </svg>
);

const GeneralSection = () => {
    const logout = useLogout();
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [message, setMessage] = useState('');
    const [success, setSuccess] = useState(false);
    const [showCurrent, setShowCurrent] = useState(false);
    const [showNew, setShowNew] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const handleChangePassword = async (e) => {
        e.preventDefault();
        setMessage('');
        if (!currentPassword || !newPassword || !confirmPassword) {
            setMessage('All fields are required.');
            setSuccess(false);
            return;
        }
        if (newPassword !== confirmPassword) {
            setMessage("New passwords don't match.");
            setSuccess(false);
            return;
        }
        setSubmitting(true);
        const { error } = await tryFetchWithAuth('/change_password', {
            method: 'POST',
            body: JSON.stringify({
                current_password: currentPassword,
                new_password: newPassword,
            }),
        });
        setSubmitting(false);
        if (error) {
            setMessage(`Error: ${error}`);
            setSuccess(false);
        } else {
            setMessage('Password changed successfully.');
            setSuccess(true);
            setCurrentPassword('');
            setNewPassword('');
            setConfirmPassword('');
        }
    };

    return (
        <div className={s.card}>
            <div className={s.cardTitle}>General</div>

            <form onSubmit={handleChangePassword}>
                <div className={s.fieldRow}>
                    <div className={s.fieldLabel}>Current password</div>
                    <input
                        className={s.input}
                        type={showCurrent ? 'text' : 'password'}
                        placeholder="Current password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                    />
                </div>
                <div className={s.showPasswordRow} onClick={() => setShowCurrent(!showCurrent)}>
                    {showCurrent ? <EyeOffIcon /> : <EyeIcon />}
                    <div>{showCurrent ? 'Hide password' : 'Show password'}</div>
                </div>

                <div className={s.twoCol}>
                    <div className={s.fieldRow}>
                        <div className={s.fieldLabel}>New password</div>
                        <input
                            className={s.input}
                            type={showNew ? 'text' : 'password'}
                            placeholder="New password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                        />
                    </div>
                    <div className={s.fieldRow}>
                        <div className={s.fieldLabel}>Confirm new password</div>
                        <input
                            className={s.input}
                            type={showNew ? 'text' : 'password'}
                            placeholder="Confirm new password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                        />
                    </div>
                </div>
                <div className={s.showPasswordRow} onClick={() => setShowNew(!showNew)}>
                    {showNew ? <EyeOffIcon /> : <EyeIcon />}
                    <div>{showNew ? 'Hide new password' : 'Show new password'}</div>
                </div>

                <div className={s.buttonRow}>
                    <button type="submit" className={s.primaryButton} disabled={submitting}>
                        {submitting ? 'Updating…' : 'Change password'}
                    </button>
                </div>
                {message && (
                    <div className={success ? s.messageSuccess : s.messageError}>{message}</div>
                )}
            </form>

            <div className={s.divider}></div>

            <div className={s.buttonRow}>
                <button className={s.dangerButton} onClick={logout}>Log out</button>
            </div>
        </div>
    );
};

const PlanSection = () => {
    const { profile } = useAuth();
    const { balance, isUnlimited, recentActivity } = useCredits();

    const currentPlan = isUnlimited ? 'Unlimited' : 'Pay As You Go';
    const subscriptionStatus = profile?.user_data?.subscription_status;
    const statusLabel = subscriptionStatus?.includes('active')
        ? 'Active'
        : subscriptionStatus?.includes('cancel')
        ? 'Cancels at period end'
        : 'Inactive';
    const statusColor = subscriptionStatus?.includes('active')
        ? '#047857'
        : subscriptionStatus?.includes('cancel')
        ? '#c0392b'
        : 'var(--color-7)';

    const openManageBillingUrl = async () => {
        const { data, error } = await tryFetchWithAuth('/manage_billing', { method: 'POST' });
        if (data?.url) window.open(data.url, '_blank');
        if (error) console.error('Error fetching manage billing URL:', error);
    };

    return (
        <div className={s.card}>
            <div className={s.cardTitle}>Plan</div>

            <div className={s.sectionLabel}>Current plan</div>
            <div className={s.planName}>{currentPlan}</div>
            {isUnlimited && subscriptionStatus && (
                <div className={s.planStatus} style={{ color: statusColor }}>{statusLabel}</div>
            )}

            <div className={s.divider}></div>

            {!isUnlimited ? (
                <>
                    <div className={s.sectionLabel}>Credit balance</div>
                    <div className={s.balanceRow}>
                        <div className={s.balanceValue}>{formatNum(balance)}</div>
                        <div className={s.balanceUnit}>credits</div>
                    </div>
                    <div className={s.helperText}>
                        1 credit = email unlock · 5 credits = mobile unlock
                    </div>

                    {Array.isArray(recentActivity) && recentActivity.length > 0 && (
                        <>
                            <div className={s.divider}></div>
                            <div className={s.sectionLabel}>Recent activity</div>
                            <div className={s.activityList} style={{ marginTop: '0.5rem' }}>
                                {recentActivity.slice(0, 5).map((row) => (
                                    <div key={row.id} className={s.activityRow}>
                                        <span>{row.kind}</span>
                                        <span className={row.delta >= 0 ? s.activityDeltaPos : s.activityDeltaNeg}>
                                            {row.delta >= 0 ? '+' : ''}{row.delta}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </>
            ) : (
                <div className={s.helperText}>Unlimited unlocks and exports.</div>
            )}

            <div className={s.divider}></div>

            <div className={s.buttonRow}>
                <Link to="/upgrade" className={s.primaryButton}>
                    {isUnlimited ? 'Manage plan' : 'Buy credits / Upgrade'}
                </Link>
                {isUnlimited && (
                    <button className={s.secondaryButton} onClick={openManageBillingUrl}>
                        Manage billing
                    </button>
                )}
            </div>
        </div>
    );
};

const SettingsPage = () => {
    const { user, profile } = useAuth();
    const [activeTab, setActiveTab] = useState('general');

    if (!user || !profile) {
        return <div className={s.loadingContainer}>Loading...</div>;
    }

    return (
        <div className={s.settingsPage}>
            <div className={s.inner}>
                <img src={process.env.PUBLIC_URL + '/logo.png'} alt="Quadratik logo" className={s.logo} />
                <h1 className={s.pageHeader}>Settings</h1>
                <p className={s.subHeader}>Manage your account and plan.</p>
            </div>
            <div className={s.grid}>
                <div className={s.sidebar}>
                    <button
                        className={`${s.sidebarItem} ${activeTab === 'general' ? s.sidebarItemActive : ''}`}
                        onClick={() => setActiveTab('general')}
                    >
                        <span className={s.sidebarIcon}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fillRule="evenodd" clipRule="evenodd" d="M11.5677 3.5C11.2129 3.5 10.8847 3.68789 10.7051 3.99377L9.89391 5.37524C9.3595 6.28538 8.38603 6.84786 7.3304 6.85645L5.73417 6.86945C5.3794 6.87233 5.0527 7.06288 4.87559 7.3702L4.43693 8.13135C4.2603 8.43784 4.25877 8.81481 4.43291 9.12273L5.22512 10.5235C5.74326 11.4397 5.74326 12.5603 5.22512 13.4765L4.43291 14.8773C4.25877 15.1852 4.2603 15.5622 4.43693 15.8687L4.87559 16.6298C5.0527 16.9371 5.3794 17.1277 5.73417 17.1306L7.33042 17.1436C8.38605 17.1522 9.35952 17.7146 9.89393 18.6248L10.7051 20.0062C10.8847 20.3121 11.2129 20.5 11.5677 20.5H12.4378C12.7926 20.5 13.1208 20.3121 13.3004 20.0062L14.1116 18.6248C14.646 17.7146 15.6195 17.1522 16.6751 17.1436L18.2714 17.1306C18.6262 17.1277 18.9529 16.9371 19.13 16.6298L19.5687 15.8687C19.7453 15.5622 19.7468 15.1852 19.5727 14.8773L18.7805 13.4765C18.2623 12.5603 18.2623 11.4397 18.7805 10.5235L19.5727 9.12273C19.7468 8.81481 19.7453 8.43784 19.5687 8.13135L19.13 7.3702C18.9529 7.06288 18.6262 6.87233 18.2714 6.86945L16.6751 6.85645C15.6195 6.84786 14.646 6.28538 14.1116 5.37524L13.3004 3.99377C13.1208 3.68788 12.7926 3.5 12.4378 3.5H11.5677Z" fill="currentColor" />
                                <path fillRule="evenodd" clipRule="evenodd" d="M12.0028 10.5C11.1741 10.5 10.5024 11.1716 10.5024 12C10.5024 12.8284 11.1741 13.5 12.0028 13.5C12.8315 13.5 13.5032 12.8284 13.5032 12C13.5032 11.1716 12.8315 10.5 12.0028 10.5Z" fill="currentColor" />
                            </svg>
                        </span>
                        <span>General</span>
                    </button>
                    <button
                        className={`${s.sidebarItem} ${activeTab === 'plan' ? s.sidebarItemActive : ''}`}
                        onClick={() => setActiveTab('plan')}
                    >
                        <span className={s.sidebarIcon}>
                            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11.5876 8.1018C11.7862 7.81202 12.2138 7.81202 12.4124 8.1018L13.4865 9.669C13.5515 9.76387 13.6473 9.83342 13.7576 9.86594L15.58 10.4031C15.9169 10.5025 16.0491 10.9092 15.8349 11.1876L14.6763 12.6934C14.6061 12.7846 14.5696 12.8971 14.5727 13.0121L14.625 14.9113C14.6346 15.2625 14.2886 15.5138 13.9576 15.3961L12.1675 14.7596C12.0592 14.721 11.9408 14.721 11.8325 14.7596L10.0424 15.3961C9.71136 15.5138 9.36537 15.2625 9.37502 14.9113L9.42727 13.0121C9.43043 12.8971 9.39386 12.7846 9.32372 12.6934L8.16515 11.1876C7.95091 10.9092 8.08307 10.5025 8.42003 10.4031L10.2424 9.86594C10.3527 9.83342 10.4485 9.76387 10.5135 9.669L11.5876 8.1018Z" fill="currentColor" />
                            </svg>
                        </span>
                        <span>Plan</span>
                    </button>
                </div>

                {activeTab === 'general' && <GeneralSection />}
                {activeTab === 'plan' && <PlanSection />}
            </div>
        </div>
    );
};

export default SettingsPage;
